/**
 * GameSortSelect · 游戏排序选择器（下拉单选）
 *
 * 用于 ChannelCompare 的 common-bar 图表模式，
 * 允许用户选择一个游戏按该游戏的指标值对渠道进行排序。
 *
 * 交互逻辑：
 * - 点击 Select 展开游戏列表
 * - 点击未选中游戏 → 选中并默认 desc，关闭下拉
 * - 点击已选中游戏 → 不触发（Select 限制），通过按钮上的排序图标切换 asc/desc
 * - 选项列表来自 filter.game_ids，通过 gameMap 渲染游戏图标和名称
 */
import SvgIcon from '@/pages/home/components/SvgIcon';
import React, { useCallback, useMemo } from 'react';
import { Select } from 'tea-component';
import { DefaultImg } from '@op/constants/defaultImg';
import RenderImg from '@op/components/RenderImg';
import type { TGameMap } from '../../../hooks/useGameMap';
import './index.scss';
import useIntls from '@opm/hooks/useIntls';

type TSortOrder = 'asc' | 'desc';

export interface IGameSortValue {
  /** 排序目标游戏 ID */
  by: string;
  /** 排序方向 */
  order: TSortOrder;
}

export interface IGameSortSelectProps {
  /** 游戏 ID 列表 */
  gameIds: string[];
  /** 游戏元信息映射 */
  gameMap: TGameMap;
  /** 当前排序状态 */
  value?: IGameSortValue;
  /** 排序变化回调 */
  onChange?: (value: IGameSortValue | Record<string, never>) => void;
  className?: string;
}

/** 排序图标映射（与 HoverSortCommonSimple 对齐） */
const sortIconMap: Record<TSortOrder | 'sort', string> = {
  sort: 'sort-icon',
  asc: 'arrow-big-up',
  desc: 'arrow-big-down',
};

export const TOTAL_VALUE = '__total__';

interface IOptionItem {
  value: string;
  text: React.ReactNode;
}

export default function GameSortSelect({
  gameIds,
  gameMap,
  value,
  onChange,
  className = '',
}: IGameSortSelectProps) {
  const currentBy = value?.by || TOTAL_VALUE;
  const currentOrder = value?.order || 'desc';
  const { intl } = useIntls();

  /** 是否为 Total 排序模式 */
  const isTotalSort = currentBy === TOTAL_VALUE;

  /** 当前选中游戏的元信息（非 Total 模式时） */
  const selectedGame = useMemo(() => {
    if (isTotalSort) return null;
    return gameMap[currentBy] || null;
  }, [currentBy, gameMap, isTotalSort]);

  /** Select value：有选中游戏用 ID，否则用 Total 占位 */
  const selectValue = currentBy;

  /** Select 选项变更 */
  const handleSelectChange = useCallback(
    (newValue: string) => {
      if (newValue === currentBy) {
        // 点击已选中的选项 → 切换 asc / desc
        onChange?.({
          by: newValue,
          order: currentOrder === 'desc' ? 'asc' : 'desc',
        });
      } else {
        // 点击未选中的选项 → 选中并默认 desc
        onChange?.({ by: newValue, order: 'desc' });
      }
    },
    [onChange, currentBy, currentOrder],
  );

  /** 构建 Select 选项列表 */
  const options = useMemo(() => {
    const list: IOptionItem[] = [
      {
        value: TOTAL_VALUE,
        text: (
          <div className="dbom-game-sort__option">
            <SvgIcon
              type={isTotalSort ? sortIconMap[currentOrder] : sortIconMap.sort}
              className={`dbom-game-sort__option-icon${isTotalSort ? ' dbom-game-sort__option-icon--active' : ''}`}
            />
            <span className="dbom-game-sort__option-name">{intl('Total')}</span>
          </div>
        ),
      },
    ];

    gameIds.forEach((gameId) => {
      const game = gameMap[gameId];
      const name = game?.game_name || gameId;
      const isSelected = currentBy === gameId;
      const iconType = isSelected
        ? sortIconMap[currentOrder || 'desc']
        : sortIconMap.sort;

      list.push({
        value: gameId,
        text: (
          <div className="dbom-game-sort__option">
            <SvgIcon
              type={iconType}
              className={`dbom-game-sort__option-icon${isSelected ? ' dbom-game-sort__option-icon--active' : ''}`}
            />
            <RenderImg
              className="dbom-game-sort__option-cover"
              coverImage={game?.cover || ''}
              defaultImage={DefaultImg.GAME}
              mode="image"
            />
            <span className="dbom-game-sort__option-name">{name}</span>
          </div>
        ),
      });
    });

    return list;
  }, [gameIds, gameMap, intl, currentBy, currentOrder]);

  /** 自定义下拉按钮渲染（对齐 ChannelSelect 的 dbom-dropdow-button label + value 布局） */
  const button = useCallback(() => {
    if (!isTotalSort && selectedGame) {
      const name = selectedGame.game_name || currentBy;
      return (
        <div className="dbom-game-sort__button">
          <span className="dbom-game-sort__button-label">{intl('Sort By')}</span>
          <div className="dbom-game-sort__button-value">
            <RenderImg
              className="dbom-game-sort__button-cover"
              coverImage={selectedGame.cover || ''}
              defaultImage={DefaultImg.GAME}
              mode="image"
            />
            <span className="dbom-game-sort__button-name">{name}</span>
            <SvgIcon
              type={sortIconMap[currentOrder]}
              className="dbom-game-sort__button-icon"
            />
          </div>
        </div>
      );
    }
    return (
      <div className="dbom-game-sort__button">
        <span className="dbom-game-sort__button-label">{intl('Sort By')}</span>
        <div className="dbom-game-sort__button-value">
          <span className="dbom-game-sort__button-text">{intl('Total')}</span>
          <SvgIcon
            type={sortIconMap[currentOrder]}
            className="dbom-game-sort__button-icon"
          />
        </div>
      </div>
    );
  }, [selectedGame, currentBy, currentOrder, intl, isTotalSort]);

  return (
    <div className={`dbom-game-sort ${className}`}>
      <Select
        appearance="button"
        options={options}
        value={selectValue}
        onChange={handleSelectChange}
        button={button}
        matchButtonWidth={false}
        virtual={false}
        boxClassName="dbom-game-sort__dropdown"
        className="dbom-game-sort__trigger"
        listHeight={300}
      />
    </div>
  );
}
